/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';

import {
  KAI_SHIKI_SETTINGS_WIDGET_COLORS,
  styleguideColors as COLORS,
  styleguideFontSize as FONT_SIZE,
  styleguideFontWeight as FONT_WEIGHT,
} from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { styleguideFontFamily as FONT, styleguideLetterSpacing as TRACKING } from '@kaiizen/kai-shiki/core';

/**
 * The shared settings widgets, in the 2026 design language (KAIDEV-4345).
 *
 * `SETTINGS_WIDGET_COLORS` keeps its interim definition, and its four carried-over fields
 * (`mainNavigationAction*` and `white`) have no reader outside this folder: `MainNavigation`
 * takes `KAI_SHIKI_SETTINGS_WIDGET_COLORS` from the seam directly
 * (`Partials/MainNavigation/mainNavigationStyles.ts`), not this constant. They are kept for
 * symmetry with the seam's shape rather than for any caller. The styles below take the 2026
 * palette and type scale from the seam directly, so moving these widgets does not move the
 * navigation with them.
 */
export const SETTINGS_WIDGET_COLORS = {
  ...KAI_SHIKI_SETTINGS_WIDGET_COLORS,
  // The 2026 ramp. The three `mainNavigationAction*` fields and `white` are left exactly as they
  // were, because `MainNavigation` reads those four and nothing else here.
  text: COLORS.ink,
  textSoft: COLORS.midtoneFaded,
  textMutedLight: COLORS.textTertiary,
  textFieldLabel: COLORS.midtone,
  border: COLORS.stroke,
  panelHeaderBackground: COLORS.shell,
  infoBlue: COLORS.info,
  infoBlueBackground: COLORS.infoFill,
  green: COLORS.success,
  disabledButtonBorder: COLORS.strokeFaded,
} as const;

export const SETTINGS_WIDGET_SPACING = {
  gutterX: 26,
  sectionInsetTop: 26,
} as const;

export const SETTINGS_WIDGET_SECTION_INSET_STYLE = css`
  label: SETTINGS_WIDGET_SECTION_INSET_STYLE;
  width: 100%;
  padding: ${SETTINGS_WIDGET_SPACING.sectionInsetTop}px ${SETTINGS_WIDGET_SPACING.gutterX}px 0;
`;

/* Sharp, as the 2026 language is: it keeps only the circle and the toggle's own pill. */
export const SETTINGS_WIDGET_PANEL_STYLE = css`
  label: SETTINGS_WIDGET_PANEL_STYLE;
  width: 100%;
  border: 1px solid ${COLORS.stroke};
  background: ${COLORS.white};
  overflow: hidden;
`;

/** The filled bar a panel sits under, as the design's locked blocks read. */
export const SETTINGS_WIDGET_PANEL_HEADER_STYLE = css`
  label: SETTINGS_WIDGET_PANEL_HEADER_STYLE;
  padding: 13px 18px;
  display: flex;
  align-items: center;
  background: ${COLORS.shell};
  border-bottom: 1px solid ${COLORS.stroke};
  font-family: ${FONT.body};
  font-size: ${pxToRem(FONT_SIZE.bodyMedium)};
  font-weight: ${FONT_WEIGHT.bold};
  line-height: 1.25;
  color: ${COLORS.ink};
`;

export const SETTINGS_WIDGET_SUBHEADING_STYLE = css`
  label: SETTINGS_WIDGET_SUBHEADING_STYLE;
  font-family: ${FONT.display};
  font-size: ${pxToRem(FONT_SIZE.displaySmall)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: -0.01em;
  line-height: 1.333;
  color: ${COLORS.ink};
`;

export const SETTINGS_WIDGET_BODY_TEXT_LIGHT_STYLE = css`
  label: SETTINGS_WIDGET_BODY_TEXT_LIGHT_STYLE;
  font-family: ${FONT.body};
  font-size: ${pxToRem(FONT_SIZE.bodySmall)};
  font-weight: ${FONT_WEIGHT.regular};
  line-height: 1.45;
  color: ${COLORS.textTertiary};
`;

export const SETTINGS_WIDGET_BODY_TEXT_STYLE = css`
  label: SETTINGS_WIDGET_BODY_TEXT_STYLE;
  font-family: ${FONT.body};
  font-size: ${pxToRem(FONT_SIZE.bodySmall)};
  font-weight: ${FONT_WEIGHT.regular};
  line-height: 1.45;
  color: ${COLORS.midtone};
`;

/* The design's page title: `.page-hdr h1` is 28 with -0.02em, not the modal-title size. */
export const SETTINGS_WIDGET_TITLE_REGULAR_STYLE = css`
  label: SETTINGS_WIDGET_TITLE_REGULAR_STYLE;
  font-family: ${FONT.display};
  font-size: ${pxToRem(FONT_SIZE.displayLarge)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: -0.02em;
  line-height: 1.2;
  color: ${COLORS.ink};
`;

/* One button shape, mono and square, at two sizes. */
const ACTION_BUTTON_BASE_STYLE = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  text-transform: uppercase;
  white-space: nowrap;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(FONT_SIZE.monoStageNum)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: ${TRACKING.monoTight};
  line-height: 1.167;
`;

export const SETTINGS_WIDGET_MEDIUM_ACTION_BUTTON_STYLE = css`
  ${ACTION_BUTTON_BASE_STYLE};
  label: SETTINGS_WIDGET_MEDIUM_ACTION_BUTTON_STYLE;
  min-height: 40px;
  padding: 11px 20px;
`;

export const SETTINGS_WIDGET_SMALL_ACTION_BUTTON_STYLE = css`
  ${ACTION_BUTTON_BASE_STYLE};
  label: SETTINGS_WIDGET_SMALL_ACTION_BUTTON_STYLE;
  min-height: 32px;
  padding: 8px 15px;
  background: transparent;
  color: ${COLORS.ink};
`;

/* The insight banner is the one blue surface the design keeps, and its text is the same blue
   family rather than the page's ink. */
export const SETTINGS_WIDGET_BANNER_TITLE_STYLE = css`
  label: SETTINGS_WIDGET_BANNER_TITLE_STYLE;
  font-family: ${FONT.body};
  font-size: ${pxToRem(FONT_SIZE.bodyMedium)};
  font-weight: ${FONT_WEIGHT.bold};
  line-height: 1.45;
  color: ${COLORS.info};
`;

export const SETTINGS_WIDGET_BANNER_BODY_STYLE = css`
  label: SETTINGS_WIDGET_BANNER_BODY_STYLE;
  font-family: ${FONT.body};
  font-size: ${pxToRem(FONT_SIZE.bodyMedium)};
  font-weight: ${FONT_WEIGHT.regular};
  line-height: 1.45;
  color: ${COLORS.info};
`;

export const SETTINGS_WIDGET_COUNT_STYLE = css`
  label: SETTINGS_WIDGET_COUNT_STYLE;
  font-family: ${FONT.body};
  font-size: ${pxToRem(FONT_SIZE.bodyMedium)};
  font-weight: ${FONT_WEIGHT.regular};
  color: ${COLORS.midtone};
  white-space: nowrap;
`;

/** The design's pill toggle: 40x22 with an 18px knob, ink when on. */
export const SETTINGS_WIDGET_TOGGLE_TRACK_STYLE = {
  width: 40,
  height: 22,
  border: 0,
  borderRadius: 11,
  background: COLORS.ink,
  padding: 0,
  position: 'relative',
  cursor: 'pointer',
} as const;

export const SETTINGS_WIDGET_TOGGLE_TRACK_OFF_STYLE = {
  background: COLORS.stroke,
} as const;

export const SETTINGS_WIDGET_TOGGLE_KNOB_STYLE = {
  position: 'absolute',
  top: 2,
  left: 20,
  width: 18,
  height: 18,
  borderRadius: '50%',
  background: COLORS.white,
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.22)',
} as const;

export const SETTINGS_WIDGET_TOGGLE_KNOB_OFF_STYLE = {
  left: 2,
} as const;
